import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import { ApiError, fetchEntitlement, openBillingPortal, startCheckout } from '../lib/api';
import { redirectTo } from '../lib/navigation';
import { usePageTitle } from '../lib/page-title';
import { PAID_PLANS_LAUNCH } from '../lib/brand';

const PLAN_LABELS = { free: 'Free', pro: 'Pro' } as const;

const MS_PER_DAY = 86_400_000;

function endsNotice(periodEnd: string): string {
  const end = new Date(periodEnd);
  const daysLeft = Math.max(0, Math.ceil((end.getTime() - Date.now()) / MS_PER_DAY));
  const when = end.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  if (daysLeft === 0) return `Your subscription ends today (${when}).`;
  return `Your subscription ends on ${when} — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left.`;
}

export function AccountPage() {
  usePageTitle('Account');
  const { session, auth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accessToken = session?.access_token;
  const checkoutOutcome = searchParams.get('checkout');
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState<ApiError | null>(null);

  const entitlement = useQuery({
    queryKey: ['entitlement', accessToken],
    queryFn: () => fetchEntitlement(accessToken as string),
    enabled: Boolean(accessToken),
    // Entitlements are webhook-derived, so after a completed checkout the
    // upgrade may land a moment after the redirect; poll until it does.
    refetchInterval: (query) =>
      checkoutOutcome === 'success' && query.state.data?.plan === 'free' ? 3000 : false,
  });

  async function signOut() {
    // Leave the protected route before the session drops; otherwise the
    // SIGNED_OUT event races this navigation and RequireAuth may land the
    // user on /signin instead of the public home page.
    await navigate('/', { replace: true });
    await auth.signOut();
  }

  async function redirectToBilling(start: (token: string) => Promise<{ url: string }>) {
    if (!accessToken) return;
    setBillingBusy(true);
    setBillingError(null);
    try {
      const { url } = await start(accessToken);
      redirectTo(url);
    } catch (error) {
      // Non-ApiError failures are treated like transient ones: retryable.
      setBillingError(error instanceof ApiError ? error : new ApiError(0));
      setBillingBusy(false);
    }
  }

  const plan = entitlement.data?.plan;
  const betaMode = entitlement.data?.beta_mode ?? false;

  return (
    <>
      <SiteHeader />
      <main className="page">
        <div className="page-head">
          <h1>Signed in</h1>
          <p className="lede">Your plan, billing, and sign-in details.</p>
        </div>

        <div className="account">
          <section className="account-row" aria-labelledby="account-profile">
            <h2 id="account-profile">Profile</h2>
            <div>
              <p className="account-email">{session?.user.email}</p>
            </div>
          </section>

          <section className="account-row" aria-label="Billing">
            <h2>Plan</h2>
            <div>
              {entitlement.isPending ? <p role="status">Loading your plan…</p> : null}
              {entitlement.isError ? <p role="alert">Could not load your plan.</p> : null}
              {plan ? (
                <p className="plan-current">
                  <strong data-testid="plan-name">{PLAN_LABELS[plan]}</strong>
                  {entitlement.data?.subscription_status &&
                  entitlement.data.subscription_status !== 'active' ? (
                    <span className="account-meta">
                      (subscription {entitlement.data.subscription_status})
                    </span>
                  ) : null}
                </p>
              ) : null}
              {betaMode ? (
                <p className="notice" role="status">
                  Beta access: every ticker is free during the beta period. No upgrade is needed.
                </p>
              ) : null}
              {plan === 'free' && !betaMode ? (
                <p className="account-meta">
                  Includes AAPL, NVDA, and GOOGL. Pro unlocks every ticker in the catalog.
                </p>
              ) : null}
              {plan === 'pro' &&
              entitlement.data?.cancel_at_period_end &&
              entitlement.data.current_period_end ? (
                <p className="notice notice-warn" role="status" data-testid="cancellation-notice">
                  {endsNotice(entitlement.data.current_period_end)} You can resume it from Manage
                  billing.
                </p>
              ) : null}
              {checkoutOutcome === 'success' && plan === 'free' ? (
                <p className="notice" role="status">
                  Thanks! Confirming your upgrade — this can take a few seconds.
                </p>
              ) : null}
              {checkoutOutcome === 'canceled' ? (
                <p className="notice notice-warn" role="status">
                  Checkout was canceled.
                </p>
              ) : null}
              <div className="button-row">
                {plan === 'free' && !betaMode ? (
                  <button
                    type="button"
                    disabled={billingBusy}
                    onClick={() => void redirectToBilling(startCheckout)}
                  >
                    Upgrade to Pro
                  </button>
                ) : null}
                {plan === 'pro' || entitlement.data?.subscription_status ? (
                  <button
                    type="button"
                    className={plan === 'free' ? 'button-quiet' : undefined}
                    disabled={billingBusy}
                    onClick={() => void redirectToBilling(openBillingPortal)}
                  >
                    Manage billing
                  </button>
                ) : null}
              </div>
              {billingError ? (
                <p className="form-error" role="alert">
                  {billingError.status === 404
                    ? `Paid plans are not available yet. Billing opens on ${PAID_PLANS_LAUNCH}.`
                    : 'Billing is temporarily unavailable. Please try again.'}
                </p>
              ) : null}
            </div>
          </section>

          <section className="account-row" aria-labelledby="account-session">
            <h2 id="account-session">Session</h2>
            <div>
              <div className="button-row">
                <Link className="button button-quiet" to="/tickers">
                  Browse tickers
                </Link>
                <button type="button" className="button-quiet" onClick={() => void signOut()}>
                  Sign out
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
